import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiPortfolioSummary, ApiPortfolioCount, MarginStatus, FilterOptions } from '@/types';
import { getMarginStatusLabel } from '@/utils/formatters';
import apiService from '@/services/apiService';
import { useComments } from '@/contexts/CommentsContext';
import CommentsSidebar from '@/components/Dashboard/CommentsSidebar';
import LCIcon from '@/components/common/LCIcon';
import {
  fmtCurrency, fmtPct, LCBtn, LCChip, LCCushionBar, LCSortHeader,
  CLS, STATUS_LABEL, STATUS_FILL,
} from '../common/LCPrimitives';

type SortField = 'client_name' | 'surplus_cushion' | 'surplus_cushion_with_cash' | 'total_aum' |
  'margin_account_equity' | 'pershing_requirement' | 'lc_margin_requirement' | 'last_updated' | 'cash_account_equity';
type SortDir = 'asc' | 'desc';

function normalizeStatus(s: string | null | undefined): string {
  if (!s) return '';
  return s.toUpperCase().replace(/ /g, '_');
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { getCommentCount, fetchCommentCountsBatch } = useComments();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rmFilter, setRmFilter] = useState('');
  const [refDate, setRefDate] = useState(new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }));
  const [sortField, setSortField] = useState<SortField>('surplus_cushion_with_cash');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [cashSortField, setCashSortField] = useState<SortField>('total_aum');
  const [cashSortDir, setCashSortDir] = useState<SortDir>('desc');

  const [portfoliosData, setPortfoliosData] = useState<ApiPortfolioSummary[]>([]);
  const [portfolioCount, setPortfolioCount] = useState<ApiPortfolioCount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [timeUntilRefresh, setTimeUntilRefresh] = useState(300);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const [commentsSidebarOpen, setCommentsSidebarOpen] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState<ApiPortfolioSummary | null>(null);

  const fetchPortfolios = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [portfolios, counts] = await Promise.all([apiService.getPortfolios(), apiService.getPortfolioCount()]);
      setPortfoliosData(portfolios);
      setPortfolioCount(counts);
      const ts = portfolios.find(p => p.last_updated)?.last_updated;
      if (ts) setLastUpdated(new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/New_York' }) + ' EST');
      fetchCommentCountsBatch(portfolios.map(p => p.portfolio_id_lig));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch portfolio data');
    } finally {
      setLoading(false);
    }
  }, [fetchCommentCountsBatch]);

  const handleRefresh = useCallback(async () => {
    await fetchPortfolios();
    setTimeUntilRefresh(300);
  }, [fetchPortfolios]);

  useEffect(() => {
    fetchPortfolios();
    const interval = setInterval(() => { if (autoRefresh) { fetchPortfolios(); setTimeUntilRefresh(300); } }, 300000);
    return () => clearInterval(interval);
  }, [fetchPortfolios, autoRefresh]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => setTimeUntilRefresh(p => (p <= 1 ? 300 : p - 1)), 1000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const onSort = (f: string) => {
    const field = f as SortField;
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const onCashSort = (f: string) => {
    const field = f as SortField;
    if (cashSortField === field) setCashSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCashSortField(field); setCashSortDir('asc'); }
  };

  const rmOptions = useMemo(() => Array.from(new Set(portfoliosData.map(p => p.rm_name).filter(Boolean) as string[])).sort(), [portfoliosData]);

  const { marginAccounts, cashAccounts } = useMemo(() => {
    const q = searchTerm.toLowerCase();
    const match = (p: ApiPortfolioSummary) => {
      if (q && !`${p.client_name} ${p.portfolio_id_lig} ${p.rm_name}`.toLowerCase().includes(q)) return false;
      if (rmFilter && p.rm_name !== rmFilter) return false;
      return true;
    };
    const marginSF = statusFilter;

    let margin = portfoliosData.filter(p => p.has_margin_account && match(p) && (!marginSF || normalizeStatus(p.status) === marginSF));
    margin.sort((a, b) => {
      const aM = a.is_margin_trading ? 1 : 0, bM = b.is_margin_trading ? 1 : 0;
      if (aM !== bM) return bM - aM;
      const av: any = a[sortField] ?? 0, bv: any = b[sortField] ?? 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    let cash = portfoliosData.filter(p => !p.has_margin_account && match(p));
    cash.sort((a, b) => {
      const av: any = a[cashSortField] ?? 0, bv: any = b[cashSortField] ?? 0;
      if (av < bv) return cashSortDir === 'asc' ? -1 : 1;
      if (av > bv) return cashSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return { marginAccounts: margin, cashAccounts: cash };
  }, [portfoliosData, searchTerm, statusFilter, rmFilter, sortField, sortDir, cashSortField, cashSortDir]);

  const stats = useMemo(() => {
    const valid = portfoliosData.filter(p => p.has_margin_account && p.total_aum != null);
    const totalAum = valid.reduce((s, p) => s + (p.total_aum || 0), 0);
    const totalMarginUsed = valid.reduce((s, p) => s + (p.lc_margin_requirement || 0), 0);
    const cushionPort = valid.filter(p => p.surplus_cushion != null && (p.pershing_requirement || 0) >= 10);
    const wsum = cushionPort.reduce((s, p) => s + (p.surplus_cushion! * (p.total_aum || 0)), 0);
    const tsum = cushionPort.reduce((s, p) => s + (p.total_aum || 0), 0);
    const avgCushion = tsum > 0 ? wsum / tsum : null;
    const counts = valid.reduce((acc, p) => {
      const s = normalizeStatus(p.status) || 'Unknown';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const totalPortfolios = portfoliosData.filter(p => p.total_aum != null).length;
    return { totalAum, totalMarginUsed, avgCushion, counts, totalPortfolios, marginPortfolios: valid.length };
  }, [portfoliosData, portfolioCount]);

  if (loading && !portfoliosData.length) {
    return (
      <div className={`${CLS.page} flex items-center justify-center min-h-[400px] flex-col gap-4`}>
        <LCIcon name="refresh" size={32} style={{ opacity: 0.4, animation: 'spin 1s linear infinite' }} />
        <span className={CLS.muted} style={{ fontSize: 14 }}>Loading portfolio data…</span>
        <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className={CLS.page}>
        <div className="bg-[rgba(229,0,37,0.06)] border border-[#E50025] px-[22px] py-[18px] mb-4">
          <div className="font-bold text-[#E50025] text-[12px] tracking-[0.12em] uppercase mb-1.5">Error Loading Data</div>
          <div style={{ fontSize: 13 }}>{error}</div>
        </div>
        <LCBtn variant="primary" size="sm" onClick={handleRefresh}>
          <LCIcon name="refresh" size={12} /> Retry
        </LCBtn>
      </div>
    );
  }

  return (
    <div className={CLS.page}>
      {/* Header */}
      <div className={CLS.pageHead}>
        <div className="flex flex-col justify-between">
          <h1 className={CLS.pageH1}>Portfolio Dashboard</h1>
          <div className={CLS.pageSub}>Real-time margin monitoring across {stats.totalPortfolios} portfolios — held at Pershing, monitored for LC house add-on requirements.</div>
        </div>
        <div className={CLS.metaRow}>
          <div className={CLS.meta}>
            <LCIcon name="timer" size={14} />
            {autoRefresh ? <>Next refresh in <strong className="text-black ml-1">{timeUntilRefresh}s</strong></> : 'Auto-refresh paused'}
          </div>
          {lastUpdated && <div className={`${CLS.meta} max-[768px]:w-full max-[768px]:justify-end`}>Last updated: <strong className="text-black ml-1">{lastUpdated}</strong></div>}
          <div className="flex gap-2">
            <LCBtn variant="outline" size="sm" onClick={() => setAutoRefresh(v => !v)}>
              <LCIcon name={autoRefresh ? 'pause' : 'play'} size={12} />
              {autoRefresh ? 'Pause' : 'Resume'}
            </LCBtn>
            <LCBtn variant="primary" size="sm" onClick={handleRefresh} disabled={loading}>
              <LCIcon name="refresh" size={12} /> Refresh
            </LCBtn>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-[18px] max-[768px]:gap-2 mb-[18px]">
        <div className="bg-white border border-black/[0.06] border-t-2 border-t-[#E50025] p-[12px_14px] flex flex-col relative min-w-0">
          <div className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">Total AUM</div>
          <div className="font-serif font-normal text-[22px] leading-tight mt-1.5 tracking-[-0.01em] text-black [font-feature-settings:'tnum']">{fmtCurrency(stats.totalAum)}</div>
          <div className="mt-1.5 text-[10.5px] text-[#7A7A76]">margin accounts</div>
        </div>
        <div className="bg-white border border-black/[0.06] p-[12px_14px] flex flex-col relative min-w-0">
          <div className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">Total Margin Used</div>
          <div className="font-serif font-normal text-[22px] leading-tight mt-1.5 tracking-[-0.01em] text-black [font-feature-settings:'tnum']">{fmtCurrency(stats.totalMarginUsed)}</div>
          <div className="mt-1.5 text-[10.5px] text-[#7A7A76]"><strong className="text-black font-semibold">{fmtPct(stats.totalAum > 0 ? stats.totalMarginUsed / stats.totalAum : 0, 1)}</strong> of AUM</div>
        </div>
        <div className="bg-white border border-black/[0.06] p-[12px_14px] flex flex-col relative min-w-0">
          <div className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">Avg. Surplus Cushion</div>
          <div className="font-serif font-normal text-[22px] leading-tight mt-1.5 tracking-[-0.01em] text-black [font-feature-settings:'tnum']">{fmtPct(stats.avgCushion, 1)}</div>
          <div className="mt-1.5 text-[10.5px] text-[#7A7A76]">weighted by AUM</div>
        </div>
        <div className="bg-white border border-black/[0.06] p-[12px_14px] flex flex-col relative min-w-0">
          <div className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">Risk Status Distribution</div>
          <div className="flex gap-0.5 mt-3.5 h-2">
            {['GREEN','AMBER_ALERT','RED_ALERT','CRITICAL','HOUSE_CALL'].map(s => {
              const c = stats.counts[s] || 0;
              const total = Object.values(stats.counts).reduce((a, b) => a + b, 0);
              const w = total ? (c / total) * 100 : 0;
              if (!c) return null;
              return <div key={s} title={`${c} ${STATUS_LABEL[s]}`} style={{ flex: w, background: STATUS_FILL[s] }} />;
            })}
          </div>
          <div className="flex flex-wrap gap-1 mt-3">
            {Object.entries(stats.counts).filter(([, n]) => n > 0).map(([s, n]) => (
              <span key={s} className="inline-flex items-center gap-[5px] text-[9.5px] font-bold tracking-[0.06em] uppercase px-[7px] py-[3px] text-white" style={{ background: STATUS_FILL[s] }}>
                <span className="w-[5px] h-[5px] rounded-full bg-white/85" />{n} {STATUS_LABEL[s]}
              </span>
            ))}
          </div>
        </div>
        <div className="bg-white border border-black/[0.06] p-[12px_14px] flex flex-col relative min-w-0">
          <div className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">Total Portfolios</div>
          <div className="font-serif font-normal text-[22px] leading-tight mt-1.5 tracking-[-0.01em] text-black [font-feature-settings:'tnum']">{stats.totalPortfolios}</div>
          <div className="mt-1.5 text-[10.5px] text-[#7A7A76]">{stats.marginPortfolios} with margin · {stats.totalPortfolios - stats.marginPortfolios} cash-only</div>
        </div>
        <div className="bg-white border border-black/[0.06] p-[12px_14px] flex flex-col relative min-w-0">
          <div className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">LC House Calls</div>
          <div className="font-serif font-normal text-[22px] leading-tight mt-1.5 tracking-[-0.01em] text-black [font-feature-settings:'tnum']">{stats.counts.HOUSE_CALL || 0}</div>
          <div className="mt-1.5 text-[10.5px] text-[#7A7A76]">require RM action by EOD</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="grid [grid-template-columns:minmax(0,1.6fr)_repeat(3,minmax(0,1fr))] gap-3 items-stretch bg-white border border-black/[0.06] p-[14px_16px] mb-[18px] max-lg:[grid-template-columns:1fr_1fr] max-[768px]:grid-cols-1">
        <div className={CLS.field}>
          <label className={CLS.fieldLabel}>Search</label>
          <div className={CLS.inputWrap}>
            <LCIcon name="search" size={14} className={CLS.inputIco} />
            <input className={`${CLS.input} pl-[38px]`} placeholder="Search by client name, portfolio ID, or RM…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
        </div>
        <div className={CLS.field}>
          <label className={CLS.fieldLabel}>Margin Status</label>
          <select className={CLS.input} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className={CLS.field}>
          <label className={CLS.fieldLabel}>Relationship Manager</label>
          <select className={CLS.input} value={rmFilter} onChange={e => setRmFilter(e.target.value)}>
            <option value="">All managers</option>
            {rmOptions.map(rm => <option key={rm} value={rm}>{rm}</option>)}
          </select>
        </div>
        <div className={CLS.field}>
          <label className={CLS.fieldLabel}>Reference Date</label>
          <div className={CLS.inputWrap}>
            <LCIcon name="calendar" size={14} className={CLS.inputIco} />
            <input className={`${CLS.input} pl-[38px]`} value={refDate} onChange={e => setRefDate(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Margin Accounts */}
      <div className={CLS.sectionTitle}>
        <h2 className={CLS.sectionH2}>Portfolios with Margin Account</h2>
        <span className={CLS.sectionCount}>{marginAccounts.length} portfolios</span>
        <span className={CLS.sectionRule} />
      </div>
      <div className={CLS.tableWrap}>
        <table className={CLS.table}>
          <thead>
            <tr>
              <th className={CLS.th} style={{ width: '11%' }}>Portfolio ID</th>
              <LCSortHeader label="Client Name" field="client_name" active={sortField === 'client_name'} dir={sortDir} onClick={onSort} />
              <th className={CLS.th}>Status</th>
              <LCSortHeader label="Surplus Cushion" field="surplus_cushion" active={sortField === 'surplus_cushion'} dir={sortDir} onClick={onSort} />
              <LCSortHeader label="With Cash" field="surplus_cushion_with_cash" active={sortField === 'surplus_cushion_with_cash'} dir={sortDir} onClick={onSort} />
              <th className={CLS.th}>Margin Trading</th>
              <LCSortHeader label="Total AUM" field="total_aum" active={sortField === 'total_aum'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
              <LCSortHeader label="Margin Equity" field="margin_account_equity" active={sortField === 'margin_account_equity'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
              <LCSortHeader label="Pershing Req." field="pershing_requirement" active={sortField === 'pershing_requirement'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
              <LCSortHeader label="LC Margin Req." field="lc_margin_requirement" active={sortField === 'lc_margin_requirement'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
              <th className={CLS.th}>RM</th>
              <th className={CLS.th} style={{ textAlign: 'center' }}>Comments</th>
              <th className={CLS.th} style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {marginAccounts.map(p => (
              <tr key={p.portfolio_id_lig} className="group cursor-pointer transition-colors hover:bg-[rgba(229,0,37,0.04)]" onClick={() => navigate(`/portfolio/${p.portfolio_id_lig}`)}>
                <td className={`${CLS.td} ${CLS.tdMono}`}>{p.portfolio_id_lig}</td>
                <td className={`${CLS.td} ${CLS.tdClient}`}>{p.client_name || '—'}</td>
                <td className={CLS.td}><LCChip status={p.status} /></td>
                <td className={CLS.td}><LCCushionBar value={p.surplus_cushion} /></td>
                <td className={CLS.td}><LCCushionBar value={p.surplus_cushion_with_cash} status={p.status} /></td>
                <td className={CLS.td}>
                  <LCChip variant={p.is_margin_trading ? 'solid-warn' : 'neutral'} label={p.is_margin_trading ? 'Yes' : 'No'} />
                </td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(p.total_aum)}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(p.margin_account_equity)}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(p.pershing_requirement)}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(p.lc_margin_requirement)}</td>
                <td className={CLS.td}>{p.rm_name || '—'}</td>
                <td className={CLS.td} style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                  <button className={CLS.iconBtn} onClick={() => { setSelectedPortfolio(p); setCommentsSidebarOpen(true); }}>
                    <LCIcon name="comments" size={16} />
                    {getCommentCount(p.portfolio_id_lig) > 0 && (
                      <span className={CLS.badgeNum}>{getCommentCount(p.portfolio_id_lig)}</span>
                    )}
                  </button>
                </td>
                <td className={CLS.td} style={{ textAlign: 'right' }}>
                  <LCIcon name="arrow" size={14} className="text-[#7A7A76] transition-all group-hover:text-[#E50025] group-hover:translate-x-0.5" />
                </td>
              </tr>
            ))}
            {marginAccounts.length === 0 && (
              <tr><td colSpan={13} className={`${CLS.td} ${CLS.muted}`} style={{ padding: 30, textAlign: 'center' }}>No margin accounts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Cash-Only Accounts */}
      <div className={CLS.sectionTitle} style={{ marginTop: 32 }}>
        <h2 className={CLS.sectionH2}>Portfolios with Cash Account Only</h2>
        <span className={CLS.sectionCount}>{cashAccounts.length} portfolios</span>
        <span className={CLS.sectionRule} />
      </div>
      <div className={CLS.tableWrap}>
        <table className={CLS.table}>
          <thead>
            <tr>
              <th className={CLS.th} style={{ width: '11%' }}>Portfolio ID</th>
              <LCSortHeader label="Client Name" field="client_name" active={cashSortField === 'client_name'} dir={cashSortDir} onClick={onCashSort} />
              <LCSortHeader label="Total AUM" field="total_aum" active={cashSortField === 'total_aum'} dir={cashSortDir} onClick={onCashSort} className={CLS.tdNum} />
              <LCSortHeader label="Cash Equity" field="cash_account_equity" active={cashSortField === 'cash_account_equity'} dir={cashSortDir} onClick={onCashSort} className={CLS.tdNum} />
              <th className={`${CLS.th} ${CLS.tdNum}`}>Cash &amp; Equiv.</th>
              <th className={CLS.th}>RM</th>
              <th className={CLS.th} style={{ textAlign: 'center' }}>Comments</th>
              <th className={CLS.th} style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {cashAccounts.map(p => (
              <tr key={p.portfolio_id_lig} className="group cursor-pointer transition-colors hover:bg-[rgba(229,0,37,0.04)]" onClick={() => navigate(`/portfolio/${p.portfolio_id_lig}`)}>
                <td className={`${CLS.td} ${CLS.tdMono}`}>{p.portfolio_id_lig}</td>
                <td className={`${CLS.td} ${CLS.tdClient}`}>{p.client_name || '—'}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(p.total_aum)}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(p.cash_account_equity)}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(p.cash_and_cash_equivalent)}</td>
                <td className={CLS.td}>{p.rm_name || '—'}</td>
                <td className={CLS.td} style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                  <button className={CLS.iconBtn} onClick={() => { setSelectedPortfolio(p); setCommentsSidebarOpen(true); }}>
                    <LCIcon name="comments" size={16} />
                    {getCommentCount(p.portfolio_id_lig) > 0 && (
                      <span className={CLS.badgeNum}>{getCommentCount(p.portfolio_id_lig)}</span>
                    )}
                  </button>
                </td>
                <td className={CLS.td} style={{ textAlign: 'right' }}>
                  <LCIcon name="arrow" size={14} className="text-[#7A7A76] transition-all group-hover:text-[#E50025] group-hover:translate-x-0.5" />
                </td>
              </tr>
            ))}
            {cashAccounts.length === 0 && (
              <tr><td colSpan={8} className={`${CLS.td} ${CLS.muted}`} style={{ padding: 30, textAlign: 'center' }}>No cash-only accounts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {commentsSidebarOpen && selectedPortfolio && (
        <CommentsSidebar
          portfolio={selectedPortfolio}
          onClose={() => { setCommentsSidebarOpen(false); setSelectedPortfolio(null); }}
        />
      )}
    </div>
  );
};

export default Dashboard;
